import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Box,
  Paper,
  TableFooter,
  Skeleton,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import Styles from './AdvancedTable.module.scss';

const AdvancedTable = ({
  columns,
  data,
  tableConfig = {},
  openLoader,
  tableTotalCount = 0,
  isServerSide = false,
  isPagination = true,
  tableOutputEvent = () => { },
  onCellClick,
}) => {
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(
    isPagination ? 10 : data.length
  );

  React.useEffect(() => {
    if (!isPagination) {
      setRowsPerPage(data.length);
    }
  }, [data.length, isPagination]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
    if (isServerSide) {
      tableOutputEvent({
        page: 1,
        limit: rowsPerPage,
        sortType: newOrder,
        sort: newOrder === 'desc' ? `-${property}` : property,
      });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (isServerSide) {
      tableOutputEvent({
        page: newPage + 1,
        limit: rowsPerPage,
        sortType: order,
        sort: order === 'desc' ? `-${orderBy}` : orderBy,
      });
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    if (isServerSide) {
      tableOutputEvent({
        page: 1,
        limit: newRowsPerPage,
        sortType: order,
        sort: order === 'desc' ? `-${orderBy}` : orderBy,
      });
    }
  };

  const processedData = React.useMemo(() => {
    if (data.length > 0 && data[data.length - 1]?.isTotal) {
      return data.slice(0, -1);
    }
    return data;
  }, [data]);

  const sortedData = React.useMemo(() => {
    if (isServerSide) {
      return processedData;
    }
    const stabilizedThis = processedData.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const orderValue = order === 'desc'
        ? (b[0][orderBy] < a[0][orderBy] ? -1 : 1)
        : (a[0][orderBy] < b[0][orderBy] ? -1 : 1);
      if (orderValue !== 0) return orderValue;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }, [processedData, order, orderBy, isServerSide]);

  const totalRow = columns.map(column => {
    if (column.isTotal) {
      if (column.totalType === 'time') {
        const totalMinutes = processedData.reduce((acc, curr) => {
          const time = String(curr[column.id] || '0.0').split('.');
          const hours = parseInt(time[0], 10) || 0;
          const minutes = parseInt(time[1], 10) || 0;
          return acc + hours * 60 + minutes;
        }, 0);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours}.${minutes.toString().padStart(2, '0')}`;
      } else {
        const total = processedData.reduce((acc, curr) => acc + (Number(curr[column.id]) || 0), 0);
        return Number(total.toFixed(2));
      }
    }
    return null;
  });

  return (
    <Paper className={Styles.renderingTable} elevation={0}>
      <TableContainer className={Styles.tableContainer}>
        <Table stickyHeader className={Styles.table}>
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={column.id}
                  sortDirection={orderBy === column.id ? order : false}
                  className={`${Styles.tableHeaderCell} ${index === 0 ? Styles.fixedColumnLeft : ''
                    } ${index === columns.length - 1 ? Styles.fixedColumnRight : ''
                    } ${index === 0 ? Styles.firstColumn : ''
                    } ${index === columns.length - 1 ? Styles.lastColumn : ''
                    }`}
                  align={column.numeric ? 'right' : 'left'}
                >
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : 'asc'}
                    onClick={(e) => handleRequestSort(e, column.id)}
                    className={Styles.sortLabel}
                  >
                    <span className={Styles.columnLabel}>{column.label}</span>
                    {orderBy === column.id ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {openLoader ? (
              // Loading skeleton rows
              [...Array(5)].map((_, rowIndex) => (
                <TableRow key={`skeleton-${rowIndex}`} className={Styles.tableRow}>
                  {columns.map((column, colIndex) => (
                    <TableCell
                      key={`skeleton-cell-${colIndex}`}
                      className={`${Styles.tableBodyCell} ${colIndex === 0 ? Styles.fixedColumnLeft : ''
                        } ${colIndex === columns.length - 1 ? Styles.fixedColumnRight : ''
                        } ${colIndex === 0 ? Styles.firstColumn : ''
                        } ${colIndex === columns.length - 1 ? Styles.lastColumn : ''
                        }`}
                    >
                      <Skeleton animation="wave" height={24} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : data.length === 0 ? (
              // No data message
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  style={{
                    padding: '40px 20px',
                    color: '#666',
                    fontSize: '16px'
                  }}
                >
                  Try to change the filter
                </TableCell>
              </TableRow>
            ) : (
              // Actual data rows
              (isServerSide ? data : sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)).map((row, rowIndex) => (
                <TableRow
                  hover
                  key={rowIndex}
                  className={`${Styles.tableRow} ${rowIndex % 2 === 0 ? Styles.evenRow : Styles.oddRow}`}
                >
                  {columns.map((column, colIndex) => (
                    <TableCell
                      key={column.id}
                      className={`${Styles.tableBodyCell} ${colIndex === 0 ? Styles.fixedColumnLeft : ''
                        } ${colIndex === columns.length - 1 ? Styles.fixedColumnRight : ''
                        } ${colIndex === 0 ? Styles.firstColumn : ''
                        } ${colIndex === columns.length - 1 ? Styles.lastColumn : ''
                        }`}
                      align={column.numeric ? 'right' : 'left'}
                      onClick={() => {
                        if (column.isClickable && onCellClick) {
                          onCellClick(row, column);
                        }
                      }}
                      style={{
                        cursor: column.isClickable && onCellClick ? 'pointer' : 'inherit',
                      }}
                    >
                      <span className={Styles.cellContent}>
                        {row[column.id]}
                      </span>
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
          <TableFooter className={Styles.tableFooter}>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={column.id}
                  className={`${Styles.tableFooterCell} ${index === 0 ? Styles.fixedColumnLeft : ''
                    } ${index === columns.length - 1 ? Styles.fixedColumnRight : ''
                    } ${index === 0 ? Styles.firstColumn : ''
                    } ${index === columns.length - 1 ? Styles.lastColumn : ''
                    }`}
                  align={column.numeric ? 'right' : 'left'}
                >
                  {totalRow[index] !== null ? (
                    <span className={Styles.totalValue}>{totalRow[index]}</span>
                  ) : ''}
                </TableCell>
              ))}
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      {isPagination && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={isServerSide ? tableTotalCount : data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          className={Styles.pagination}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      )}
    </Paper>
  );
};

export default AdvancedTable;